/**
 * Auth Page - Security Clearance Terminal
 * ========================================
 * Login or Register to access the mecha-garage!
 * Looks like a security checkpoint.
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Terminal, 
  Eye, 
  EyeOff, 
  Loader2,
  Shield,
  User,
  Mail,
  Lock,
  ChevronLeft,
  AlertCircle
} from "lucide-react";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, login, register } = useAuth();
  
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "register") {
        await register(formData.username, formData.email, formData.password);
      } else {
        await login(formData.email, formData.password);
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  return (
    <div className="min-h-screen bg-[#0B0D10] relative overflow-hidden flex items-center justify-center">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1616506436028-1980ead3ebe7?crop=entropy&cs=srgb&fm=jpg&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-[#0B0D10]/80" />
      <div className="absolute inset-0 grid-pattern opacity-20" />

      {/* Back Button */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-muted-foreground hover:text-white transition-colors font-mono text-sm"
        data-testid="back-to-home-btn"
      >
        <ChevronLeft className="w-4 h-4" />
        BACK TO BASE
      </button>

      {/* Auth Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 border border-primary/30 mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 
            className="text-3xl font-black text-white tracking-tight uppercase"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="auth-title"
          >
            {mode === "login" ? "SECURITY CLEARANCE" : "NEW RECRUIT"}
          </h1>
          <p className="text-muted-foreground font-mono text-sm mt-2">
            {mode === "login" 
              ? "Enter your credentials to access the mecha-garage" 
              : "Register to join the CodeForge team"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#16191F] border border-white/10 p-8 corner-brackets">
          {/* Mode Toggle */}
          <div className="flex mb-6 bg-black/30 p-1">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-sm font-mono uppercase transition-colors ${
                mode === "login" 
                  ? "bg-primary text-black" 
                  : "text-muted-foreground hover:text-white"
              }`}
              data-testid="login-tab-btn"
            >
              LOGIN
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`flex-1 py-2 text-sm font-mono uppercase transition-colors ${
                mode === "register" 
                  ? "bg-primary text-black" 
                  : "text-muted-foreground hover:text-white"
              }`}
              data-testid="register-tab-btn"
            >
              REGISTER
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400 text-sm font-mono">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username (Register only) */}
            {mode === "register" && (
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  CALLSIGN (USERNAME)
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="FrickOrFrack"
                    className="pl-10 bg-black/50 border-b-2 border-muted focus:border-primary rounded-none h-12 font-mono text-white placeholder:text-muted-foreground/50"
                    required
                    minLength={3}
                    maxLength={20}
                    data-testid="username-input"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                EMAIL ADDRESS
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="hacker@codeforge.dev"
                  className="pl-10 bg-black/50 border-b-2 border-muted focus:border-primary rounded-none h-12 font-mono text-white placeholder:text-muted-foreground/50"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                ACCESS CODE (PASSWORD)
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-black/50 border-b-2 border-muted focus:border-primary rounded-none h-12 font-mono text-white placeholder:text-muted-foreground/50"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary text-black font-bold uppercase tracking-wider hover:bg-primary/90 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)] active:translate-y-[2px] active:shadow-none transition-all"
              data-testid="submit-auth-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {mode === "login" ? "AUTHENTICATING..." : "REGISTERING..."}
                </>
              ) : (
                <>
                  <Terminal className="w-4 h-4 mr-2" />
                  {mode === "login" ? "ACCESS TERMINAL" : "CREATE ACCOUNT"}
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <p className="text-xs text-muted-foreground font-mono">
              {mode === "login" ? (
                <>
                  New to CodeForge?{" "}
                  <button 
                    onClick={() => setMode("register")}
                    className="text-primary hover:underline"
                    data-testid="switch-to-register"
                  >
                    Register here
                  </button>
                </>
              ) : (
                <>
                  Already have access?{" "}
                  <button 
                    onClick={() => setMode("login")}
                    className="text-primary hover:underline"
                    data-testid="switch-to-login"
                  >
                    Login here
                  </button>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-xs text-muted-foreground font-mono mt-6">
          Tip: Your password should be at least 6 characters long
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
